import api from './api';

export interface User {
  id: number;
  email: string;
  role: 'admin' | 'user';
  email_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    token: string;
    user: User;
  };
  message: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  role?: 'admin' | 'user';
}

export interface RegisterResponse {
  success: boolean;
  data: {
    user: User;
  };
  message: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export interface Session {
  id: number;
  created_at: string;
  last_activity: string;
  expires_at: string;
  ip_address: string;
  user_agent: string;
}

export interface SessionsResponse {
  success: boolean;
  data: {
    sessions: Session[];
    total: number;
  };
}

export interface ApiError {
  success: false;
  error: string;
  code: string;
  details?: {
    errors: Array<{
      field: string;
      message: string;
      value?: any;
    }>;
  };
}

class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', credentials);

    if (response.data.success && response.data.data.token) {
      this.setAuthToken(response.data.data.token);
      this.setUser(response.data.data.user);
    }

    return response.data;
  }

  /**
   * Register new user (Admin only)
   */
  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponse>('/auth/register', userData);
    return response.data;
  }

  /**
   * Logout current session
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Logout from all devices
   */
  async logoutAll(): Promise<void> {
    try {
      await api.post('/auth/logout-all');
    } catch (error) {
      console.error('Logout all error:', error);
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', { email });
    return response.data;
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await api.post<ResetPasswordResponse>('/auth/reset-password', data);
    return response.data;
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<{ success: boolean; data: User }>('/auth/me');

    if (response.data.success && response.data.data) {
      this.setUser(response.data.data);
      return response.data.data;
    }

    throw new Error('Failed to get user info');
  }

  /**
   * Refresh JWT token
   */
  async refreshToken(): Promise<string> {
    const response = await api.post<{ success: boolean; data: { token: string } }>('/auth/refresh');

    if (response.data.success && response.data.data.token) {
      this.setAuthToken(response.data.data.token);
      return response.data.data.token;
    }

    throw new Error('Failed to refresh token');
  }

  /**
   * Get all active sessions
   */
  async getSessions(): Promise<SessionsResponse> {
    const response = await api.get<SessionsResponse>('/auth/sessions');
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Check if user has admin role
   */
  isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin';
  }

  /**
   * Get stored auth token
   */
  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Get stored user data
   */
  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr) as User;
      } catch (error) {
        console.error('Failed to parse user data:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Set auth token in localStorage
   */
  private setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  /**
   * Set user data in localStorage
   */
  private setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  /**
   * Clear all auth data from localStorage
   */
  private clearAuth(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
